import { NormalizedEntityItem } from '@/lib/edlink-utils';
import { DeltaKind } from '@airreading/common/data';
import { ChevronDown, ChevronLeft } from 'lucide-react';
import { Fragment, useState } from 'react';

type DeltaKindConfigProps = {
  color: string;
  label: (isDryRun: boolean) => string;
};

const DELTA_KIND_CONFIG: Record<DeltaKind, DeltaKindConfigProps> = {
  create: {
    color: 'border-l-green-500',
    label: (isDryRun) => (isDryRun ? 'Creations' : 'Created items'),
  },
  update: {
    color: 'border-l-blue-500',
    label: (isDryRun) => (isDryRun ? 'Updates' : 'Updated items'),
  },
  delete: {
    color: 'border-l-red-500',
    label: (isDryRun) => (isDryRun ? 'Deletions' : 'Deleted items'),
  },
};

interface DeltaKindGroupProps {
  kind: DeltaKind;
  items: NormalizedEntityItem[];
  isDryRun: boolean;
}

export function DeltaKindGroup({ kind, items, isDryRun }: DeltaKindGroupProps) {
  const [expanded, setExpanded] = useState(kind === 'delete');
  const { color, label } = DELTA_KIND_CONFIG[kind];
  const hasItems = items.length > 0;

  return (
    <div className={`border-l-4 ${color} pl-3 bg-gray-100`}>
      <button
        type="button"
        className="flex w-full items-center justify-between py-1 text-sm font-medium"
        onClick={() => hasItems && setExpanded((v) => !v)}
        disabled={!hasItems}
      >
        <span>
          {label(isDryRun)}: {items.length}
        </span>
        {hasItems && (expanded ? <ChevronDown className="h-4 w-4 mr-2" /> : <ChevronLeft className="h-4 w-4 mr-2" />)}
      </button>
      {expanded && hasItems && (
        <div className="mt-2">
          <ul className="grid grid-cols-2 gap-x-4 gap-y-1 ml-4 pb-2 text-sm text-muted-foreground">
            {items.map((item, i) => (
              <li key={i} className="list-disc list-inside">
                <span title={item.label}>{item.label}</span>
                {item.details && (
                  <dl className="ml-5 mt-1 grid grid-cols-[auto_1fr] gap-x-2 text-xs text-muted-foreground/70">
                    {Object.entries(item.details).map(([itemKey, itemValue]) => (
                      <Fragment key={`${i}-${itemKey}`}>
                        <dt className="font-medium">{itemKey}:</dt>
                        <dd className="truncate" title={itemValue}>
                          {itemValue}
                        </dd>
                      </Fragment>
                    ))}
                  </dl>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
